/**
 * DocumentPartiesEditor — F20 Etap 3b
 *
 * Edytor stron dokumentu. Reużywalny komponent — montowany przez formularz
 * szczegółów Umowy i Pisma (i przyszłych klas). Operuje na istniejącym
 * dokumencie (documentId).
 *
 * Kontrakt API:
 *  GET  /documents/:id/parties  → { roles, parties }
 *  POST /documents/:id/parties  → 201 | 409 CARDINALITY_EXCEEDED | 422 ROLE_CLASS_MISMATCH | 422 IDENTITY_*
 *  PATCH /documents/:id/parties/:partyId → 200 | 409 PRIMARY_RACE
 *  DELETE /documents/:id/parties/:partyId → 200 { deleted: true }
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ContractorFormModal } from '@/components/contractors/ContractorFormModal';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { RelationPicker } from '@/components/ui/RelationPicker';
import { useToast } from '@/contexts/ToastContext';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { api } from '@/lib/api';
import { getSystemEntityAdapter } from '@/lib/system-entity-adapters';
import { useCompanies } from '@/lib/use-permissions';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface DocumentRole {
  id: string;
  code: string;
  name: string;
  cardinalityMax: number | null;
  isActive: boolean;
  sortOrder: number;
}

export type PartyKind = 'contractor' | 'company' | 'external';

export interface DocumentParty {
  id: string;
  documentId: string;
  partyKind: PartyKind;
  contractorId: string | null;
  companyId: string | null;
  roleId: string;
  isPrimary: boolean;
  sortOrder: number;
  partyName: string | null;
  partyTaxId: string | null;
  partyRegon: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PartiesResponse {
  roles: DocumentRole[];
  parties: DocumentParty[];
}

interface AddPartyBody {
  roleCode: string;
  partyKind: PartyKind;
  contractorId?: string;
  companyId?: string;
  partyName?: string;
  partyTaxId?: string;
  partyRegon?: string;
  isPrimary?: boolean;
  sortOrder?: number;
}

interface EditPartyBody {
  partyName?: string;
  partyTaxId?: string;
  partyRegon?: string;
  sortOrder?: number;
}

/** Body wysyłany do PATCH strony — edycja snapshotu LUB ustawienie strony głównej (wspólny endpoint). */
interface PatchPartyBody extends EditPartyBody {
  isPrimary?: boolean;
}

// ─── Helper — map API error code to i18n key ─────────────────────────────────

function resolveApiErrorKey(code: string | undefined): string {
  switch (code) {
    case 'CARDINALITY_EXCEEDED':
      return 'tenant.documents.parties.errors.cardinalityExceeded';
    case 'ROLE_CLASS_MISMATCH':
      return 'tenant.documents.parties.errors.roleClassMismatch';
    case 'IDENTITY_ARM_INVALID':
      return 'tenant.documents.parties.errors.identityArmInvalid';
    case 'IDENTITY_TARGET_NOT_FOUND':
      return 'tenant.documents.parties.errors.identityTargetNotFound';
    case 'PRIMARY_RACE':
      return 'tenant.documents.parties.errors.primaryRace';
    default:
      return 'tenant.documents.parties.errors.generic';
  }
}

function extractApiErrorCode(err: unknown): string | undefined {
  if (
    err &&
    typeof err === 'object' &&
    'response' in err &&
    err.response &&
    typeof err.response === 'object' &&
    'data' in err.response &&
    err.response.data &&
    typeof err.response.data === 'object'
  ) {
    const data = err.response.data as { code?: string; error?: string };
    const value = data.code ?? data.error;
    if (value !== undefined) return String(value);
  }
  return undefined;
}

// ─── Sub-component: Add party form ──────────────────────────────────────────

interface AddPartyFormProps {
  documentId: string;
  roles: DocumentRole[];
  parties: DocumentParty[];
  onSuccess: () => void;
  /**
   * Firma DOKUMENTU (nie firma-jako-strona, patrz lokalny stan `companyId`
   * niżej) — company scoping, przekazana do `RelationPicker` (kontrahent)
   * jako `?companyId=`. `undefined`/`null` = picker nieograniczony.
   *
   * DECYZJA WŁAŚCICIELA (2026-08-03): strona dokumentu = kontrahent FIRMY DOKUMENTU —
   * lista przy dodawaniu strony jest celowo zawężona do firmy dokumentu (spójnie
   * z pickerem dostawcy na fakturze i widocznością per firma), a nie do unii firm usera.
   */
  documentCompanyId?: string | null;
}

function AddPartyForm({
  documentId,
  roles,
  parties,
  onSuccess,
  documentCompanyId,
}: AddPartyFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reportRecordLock = useReportRecordLock();

  const [roleCode, setRoleCode] = useState('');
  const [partyKind, setPartyKind] = useState<PartyKind>('contractor');
  const [contractorId, setContractorId] = useState<string | null>(null);
  // Okno formatki kontrahenta (dodaj/edytuj) osadzone nad dropdownem wyboru — `null` = zamknięte.
  const [contractorForm, setContractorForm] = useState<{
    mode: 'create' | 'edit';
    contractorId?: string;
  } | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [partyName, setPartyName] = useState('');
  const [partyTaxId, setPartyTaxId] = useState('');
  const [partyRegon, setPartyRegon] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  // Resolve contractor display name for snapshot — mirrors RelationPicker's selectedFetched logic.
  const contractorAdapter = getSystemEntityAdapter('contractors');
  const { data: contractorDetailData } = useQuery<string | null>({
    queryKey: ['party-contractor-name', contractorId],
    queryFn: async () => {
      if (!contractorId || !contractorAdapter) return null;
      try {
        if (!contractorAdapter.detailPath || !contractorAdapter.detailResponseKey) return null;
        const res = await api.get<Record<string, unknown>>(
          contractorAdapter.detailPath(contractorId),
        );
        const raw = res.data[contractorAdapter.detailResponseKey] as
          | Record<string, unknown>
          | undefined;
        return raw ? contractorAdapter.getLabel(raw) : null;
      } catch {
        return null;
      }
    },
    enabled: !!contractorId,
    staleTime: 60_000,
  });

  const companiesQ = useCompanies();
  const activeCompanies = (companiesQ.data?.companies ?? []).filter((c) => c.isActive);

  // Compute roles at cardinalityMax to disable them in the picker.
  const roleSaturated = new Set<string>();
  for (const role of roles) {
    if (role.cardinalityMax !== null) {
      const count = parties.filter((p) => p.roleId === role.id).length;
      if (count >= role.cardinalityMax) {
        roleSaturated.add(role.code);
      }
    }
  }

  const addMutation = useMutation({
    mutationFn: async (body: AddPartyBody) => {
      const res = await api.post<DocumentParty>(`/documents/${documentId}/parties`, body);
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.parties.toast.addSuccess'),
      });
      resetForm();
      onSuccess();
    },
    onError: (err: unknown) => {
      // Zmiana stron to zapis dokumentu — na czas zadania przycisku SQL na nim serwer odpowiada 409
      // `RECORD_LOCKED_BY_RUN`: opis zadania zamiast ogólnego błędu.
      if (reportRecordLock(err)) return;
      const code = extractApiErrorCode(err);
      toast.show({
        variant: 'error',
        title: t(resolveApiErrorKey(code)),
      });
    },
  });

  function resetForm() {
    setRoleCode('');
    setPartyKind('contractor');
    setContractorId(null);
    setCompanyId('');
    setPartyName('');
    setPartyTaxId('');
    setPartyRegon('');
    setIsPrimary(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const selectedRole = roles.find((r) => r.code === roleCode);
    if (!selectedRole) return;

    const body: AddPartyBody = {
      roleCode,
      partyKind,
      isPrimary: isPrimary || undefined,
    };

    if (partyKind === 'contractor') {
      if (!contractorId) {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.parties.errors.contractorRequired'),
        });
        return;
      }
      body.contractorId = contractorId;
      // Snapshot the contractor's display name so identityDisplay shows it without a UUID fallback.
      const resolvedName = contractorDetailData ?? null;
      if (resolvedName) body.partyName = resolvedName;
    } else if (partyKind === 'company') {
      if (!companyId) {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.parties.errors.companyRequired'),
        });
        return;
      }
      body.companyId = companyId;
      // Snapshot the company's legal name so identityDisplay shows it without a UUID fallback.
      const company = activeCompanies.find((c) => c.id === companyId);
      const resolvedCompanyName = company?.legalName ?? company?.shortName ?? null;
      if (resolvedCompanyName) body.partyName = resolvedCompanyName;
    } else {
      // external
      if (!partyName.trim()) {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.parties.errors.partyNameRequired'),
        });
        return;
      }
      body.partyName = partyName.trim();
      if (partyTaxId.trim()) body.partyTaxId = partyTaxId.trim();
      if (partyRegon.trim()) body.partyRegon = partyRegon.trim();
    }

    addMutation.mutate(body);
  }

  const labelClass = 'block text-xs font-medium text-text-secondary mb-1';

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="add-party-form"
      className="rounded-lg border border-border-primary bg-bg-secondary p-4 space-y-4"
    >
      <p className="text-sm font-semibold text-text-primary">
        {t('tenant.documents.parties.addTitle')}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Role */}
        <div>
          <label className={labelClass}>{t('tenant.documents.parties.fields.role')}</label>
          <Dropdown
            value={roleCode}
            onChange={(v) => setRoleCode(v)}
            data-testid="add-party-role"
            options={[
              { value: '', label: t('tenant.documents.parties.fields.rolePlaceholder') },
              ...roles.map((r) => ({
                value: r.code,
                label: roleSaturated.has(r.code)
                  ? `${r.name} (${t('tenant.documents.parties.fields.roleFull')})`
                  : r.name,
                disabled: roleSaturated.has(r.code),
              })),
            ]}
          />
        </div>

        {/* Party kind */}
        <div>
          <label className={labelClass}>{t('tenant.documents.parties.fields.partyKind')}</label>
          <Dropdown
            value={partyKind}
            onChange={(v) => setPartyKind(v as PartyKind)}
            data-testid="add-party-kind"
            options={[
              { value: 'contractor', label: t('tenant.documents.parties.kinds.contractor') },
              { value: 'company', label: t('tenant.documents.parties.kinds.company') },
              { value: 'external', label: t('tenant.documents.parties.kinds.external') },
            ]}
          />
        </div>
      </div>

      {/* Identity by kind */}
      {partyKind === 'contractor' && (
        <div>
          <label className={labelClass}>{t('tenant.documents.parties.fields.contractor')}</label>
          <RelationPicker
            systemModuleSlug="contractors"
            value={contractorId}
            onChange={(v) => setContractorId(typeof v === 'string' ? v : null)}
            placeholder={t('tenant.documents.parties.fields.contractorPlaceholder')}
            companyId={documentCompanyId}
            data-testid="add-party-contractor"
            recordActions={{
              // „Przejdź do kartoteki" — pełna kartoteka pod route'em (opuszcza dokument).
              onOpenCard: (id) => navigate(`/contractors/${id}`),
              // „Edytuj" — formatka kontrahenta w modalu, bez wychodzenia z dokumentu.
              onEditRecord: (id) => setContractorForm({ mode: 'edit', contractorId: id }),
              // „Dodaj nowego" — formatka create; po zapisie wybieramy nowy rekord.
              onCreateNew: () => setContractorForm({ mode: 'create' }),
              labels: {
                openCard: t('tenant.contractors.pickerActions.openCard'),
                editRecord: t('tenant.contractors.pickerActions.editSelected'),
                createNew: t('tenant.contractors.pickerActions.addNew'),
              },
            }}
          />
          <ContractorFormModal
            open={contractorForm !== null}
            mode={contractorForm?.mode ?? 'create'}
            contractorId={contractorForm?.contractorId}
            companyId={documentCompanyId ?? undefined}
            onClose={() => setContractorForm(null)}
            onSaved={(id) => {
              const mode = contractorForm?.mode;
              if (mode === 'create') {
                // Nowy kontrahent → wybierz go jako stronę dokumentu i zamknij formatkę.
                setContractorId(id);
                setContractorForm(null);
                return;
              }
              // Edit: odśwież wyświetlaną nazwę wybranego kontrahenta (snapshot liczy backend).
              queryClient.invalidateQueries({ queryKey: ['party-contractor-name'] });
            }}
            data-testid="add-party-contractor-form"
          />
        </div>
      )}

      {partyKind === 'company' && (
        <div>
          <label className={labelClass}>{t('tenant.documents.parties.fields.company')}</label>
          <Dropdown
            value={companyId}
            onChange={(v) => setCompanyId(v)}
            data-testid="add-party-company"
            options={[
              { value: '', label: t('tenant.documents.parties.fields.companyPlaceholder') },
              ...activeCompanies.map((c) => ({
                value: c.id,
                label: `${c.shortName ?? c.legalName}${c.taxId ? ` (${c.taxId})` : ''}`,
              })),
            ]}
          />
        </div>
      )}

      {partyKind === 'external' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <label className={labelClass}>
              {t('tenant.documents.parties.fields.partyName')}
              <span className="ml-0.5 text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              data-testid="add-party-external-name"
              placeholder={t('tenant.documents.parties.fields.partyNamePlaceholder')}
            />
          </div>
          <div>
            <label className={labelClass}>{t('tenant.documents.parties.fields.taxId')}</label>
            <Input
              type="text"
              value={partyTaxId}
              onChange={(e) => setPartyTaxId(e.target.value)}
              data-testid="add-party-external-taxid"
              placeholder="NIP"
            />
          </div>
          <div>
            <label className={labelClass}>{t('tenant.documents.parties.fields.regon')}</label>
            <Input
              type="text"
              value={partyRegon}
              onChange={(e) => setPartyRegon(e.target.value)}
              data-testid="add-party-external-regon"
              placeholder="REGON"
            />
          </div>
        </div>
      )}

      {/* isPrimary checkbox */}
      <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
          data-testid="add-party-is-primary"
          className="h-4 w-4 rounded border-border-secondary text-accent-600 focus:ring-accent-500"
        />
        {t('tenant.documents.parties.fields.isPrimary')}
      </label>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={resetForm}
          className="rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={addMutation.isPending}
          data-testid="add-party-submit"
          className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-60"
        >
          {addMutation.isPending
            ? t('tenant.documents.parties.adding')
            : t('tenant.documents.parties.addButton')}
        </button>
      </div>
    </form>
  );
}

// ─── Sub-component: Edit party modal (inline) ────────────────────────────────

interface EditPartyInlineProps {
  party: DocumentParty;
  onSave: (body: EditPartyBody) => void;
  onCancel: () => void;
  isPending: boolean;
}

function EditPartyInline({ party, onSave, onCancel, isPending }: EditPartyInlineProps) {
  const { t } = useTranslation();
  const [partyName, setPartyName] = useState(party.partyName ?? '');
  const [partyTaxId, setPartyTaxId] = useState(party.partyTaxId ?? '');
  const [partyRegon, setPartyRegon] = useState(party.partyRegon ?? '');

  // Only external parties have editable snapshot fields; for contractor/company
  // the identity arm is not re-editable per the API contract. We still allow
  // editing partyName/taxId/regon even for contractor/company in case the
  // server stores an override snapshot (the PATCH accepts them regardless).

  const labelClass = 'block text-xs font-medium text-text-secondary mb-1';

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const body: EditPartyBody = {};
    if (partyName.trim()) body.partyName = partyName.trim();
    if (partyTaxId.trim()) body.partyTaxId = partyTaxId.trim();
    if (partyRegon.trim()) body.partyRegon = partyRegon.trim();
    onSave(body);
  }

  return (
    <form
      onSubmit={handleSave}
      data-testid={`edit-party-form-${party.id}`}
      className="mt-2 space-y-2 rounded-lg border border-border-primary bg-bg-secondary p-3"
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <label className={labelClass}>{t('tenant.documents.parties.fields.partyName')}</label>
          <Input
            type="text"
            value={partyName}
            onChange={(e) => setPartyName(e.target.value)}
            data-testid={`edit-party-name-${party.id}`}
          />
        </div>
        <div>
          <label className={labelClass}>{t('tenant.documents.parties.fields.taxId')}</label>
          <Input
            type="text"
            value={partyTaxId}
            onChange={(e) => setPartyTaxId(e.target.value)}
            data-testid={`edit-party-taxid-${party.id}`}
          />
        </div>
        <div>
          <label className={labelClass}>{t('tenant.documents.parties.fields.regon')}</label>
          <Input
            type="text"
            value={partyRegon}
            onChange={(e) => setPartyRegon(e.target.value)}
            data-testid={`edit-party-regon-${party.id}`}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border-secondary px-3 py-1 text-sm text-text-primary hover:bg-bg-secondary"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          data-testid={`edit-party-save-${party.id}`}
          className="rounded-lg bg-accent-600 px-3 py-1 text-sm text-white hover:bg-accent-700 disabled:opacity-60"
        >
          {isPending ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export interface DocumentPartiesEditorProps {
  documentId: string;
  /** When false — read-only view (list only, no add/edit/delete/set-primary controls). */
  canManage: boolean;
  /**
   * Firma DOKUMENTU (T3 company scoping) — przekazana do `RelationPicker`
   * (kontrahent) w formularzu dodawania strony, jako `?companyId=`. Rodzic
   * (ContractDetail/LetterDetail) przekazuje firmę zapisanego rekordu — ten
   * edytor jest gated `!isCreate && !!id`, więc to zawsze firma REKORDU, nie
   * kreatora typu. `undefined`/`null` = picker nieograniczony.
   */
  companyId?: string | null;
}

export function DocumentPartiesEditor({
  documentId,
  canManage,
  companyId,
}: DocumentPartiesEditorProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const reportRecordLock = useReportRecordLock();

  const QUERY_KEY = ['document-parties', documentId] as const;

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPartyId, setEditingPartyId] = useState<string | null>(null);
  const [pendingDeleteParty, setPendingDeleteParty] = useState<DocumentParty | null>(null);

  // ── Fetch ──
  const { data, isLoading, isError } = useQuery<PartiesResponse>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<PartiesResponse>(`/documents/${documentId}/parties`);
      return res.data;
    },
    staleTime: 30_000,
  });

  const roles = data?.roles ?? [];
  const parties = data?.parties ?? [];

  // ── Helper: label for a role id ──
  function roleLabel(roleId: string): string {
    return roles.find((r) => r.id === roleId)?.name ?? roleId.slice(0, 8) + '…';
  }

  // ── Helper: identity display for a party ──
  function identityDisplay(party: DocumentParty): string {
    if (party.partyName) return party.partyName;
    if (party.contractorId)
      return `(${t('tenant.documents.parties.kinds.contractor')} ${party.contractorId.slice(0, 8)}…)`;
    if (party.companyId)
      return `(${t('tenant.documents.parties.kinds.company')} ${party.companyId.slice(0, 8)}…)`;
    return '—';
  }

  // ── Invalidate & refetch ──
  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }

  // ── PATCH mutation (edit snapshot + set-primary share the same endpoint) ──
  const patchMutation = useMutation({
    mutationFn: async ({ partyId, body }: { partyId: string; body: PatchPartyBody }) => {
      const res = await api.patch<DocumentParty>(
        `/documents/${documentId}/parties/${partyId}`,
        body,
      );
      return res.data;
    },
    onSuccess: (_data, { body }) => {
      if (body.isPrimary === true) {
        toast.show({
          variant: 'success',
          title: t('tenant.documents.parties.toast.setPrimarySuccess'),
        });
      } else {
        toast.show({
          variant: 'success',
          title: t('tenant.documents.parties.toast.editSuccess'),
        });
        setEditingPartyId(null);
      }
      invalidate();
    },
    onError: (err: unknown, { body }) => {
      // Zmiana strony dokumentu w trakcie zadania przycisku SQL na nim (409) — opis zadania.
      if (reportRecordLock(err)) return;
      const code = extractApiErrorCode(err);
      if (code === 'PRIMARY_RACE' && body.isPrimary === true) {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.parties.errors.primaryRace'),
        });
      } else {
        toast.show({
          variant: 'error',
          title: t(resolveApiErrorKey(code)),
        });
      }
    },
  });

  // ── DELETE mutation ──
  const deleteMutation = useMutation({
    mutationFn: async (partyId: string) => {
      await api.delete(`/documents/${documentId}/parties/${partyId}`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.parties.toast.deleteSuccess'),
      });
      setPendingDeleteParty(null);
      invalidate();
    },
    onError: (err: unknown) => {
      // Usunięcie strony w trakcie zadania przycisku SQL na dokumencie (409) — opis zadania.
      if (!reportRecordLock(err)) {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.parties.toast.deleteError'),
        });
      }
      setPendingDeleteParty(null);
    },
  });

  // ── Render states ──
  if (isLoading) {
    return (
      <div
        className="animate-pulse space-y-2"
        data-testid="document-parties-loading"
        aria-busy="true"
      >
        <div className="h-10 rounded bg-bg-secondary" />
        <div className="h-10 rounded bg-bg-secondary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700"
        data-testid="document-parties-error"
      >
        {t('tenant.documents.parties.loadError')}
      </div>
    );
  }

  // ── Main render ──
  return (
    <section data-testid="document-parties-editor" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          {t('tenant.documents.parties.title')}
        </h3>
        {canManage && !showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            data-testid="add-party-open-btn"
            className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700"
          >
            {t('tenant.documents.parties.addButton')}
          </button>
        )}
      </div>

      {/* Party list */}
      {parties.length === 0 ? (
        <p className="text-sm text-text-muted" data-testid="document-parties-empty">
          {t('tenant.documents.parties.empty')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="document-parties-list">
          {parties.map((party) => (
            <li
              key={party.id}
              data-testid={`party-row-${party.id}`}
              className="rounded-lg border border-border-primary bg-bg-primary p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                {/* Left: role + identity */}
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="text-xs font-medium text-accent-700"
                      data-testid={`party-role-${party.id}`}
                    >
                      {roleLabel(party.roleId)}
                    </span>
                    {party.isPrimary && (
                      <span
                        className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700"
                        data-testid={`party-primary-badge-${party.id}`}
                      >
                        {t('tenant.documents.parties.primaryBadge')}
                      </span>
                    )}
                  </div>
                  <p
                    className="text-sm text-text-primary truncate"
                    data-testid={`party-identity-${party.id}`}
                  >
                    {identityDisplay(party)}
                  </p>
                  {party.partyTaxId && (
                    <p className="text-xs text-text-muted">
                      {t('tenant.documents.parties.fields.taxId')}: {party.partyTaxId}
                    </p>
                  )}
                </div>

                {/* Right: actions (only when canManage) */}
                {canManage && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!party.isPrimary && (
                      <button
                        type="button"
                        onClick={() =>
                          patchMutation.mutate({ partyId: party.id, body: { isPrimary: true } })
                        }
                        disabled={patchMutation.isPending}
                        data-testid={`set-primary-btn-${party.id}`}
                        className="rounded px-2 py-1 text-xs text-accent-600 hover:bg-accent-50 disabled:opacity-60"
                      >
                        {t('tenant.documents.parties.actions.setPrimary')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setEditingPartyId(editingPartyId === party.id ? null : party.id)
                      }
                      data-testid={`edit-party-btn-${party.id}`}
                      className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-bg-secondary"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteParty(party)}
                      data-testid={`delete-party-btn-${party.id}`}
                      className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>

              {/* Inline edit form */}
              {canManage && editingPartyId === party.id && (
                <EditPartyInline
                  party={party}
                  isPending={patchMutation.isPending}
                  onSave={(body) => patchMutation.mutate({ partyId: party.id, body })}
                  onCancel={() => setEditingPartyId(null)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add form (shown inline below list) */}
      {canManage && showAddForm && (
        <AddPartyForm
          documentId={documentId}
          roles={roles}
          parties={parties}
          documentCompanyId={companyId}
          onSuccess={() => {
            invalidate();
            setShowAddForm(false);
          }}
        />
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={pendingDeleteParty !== null}
        variant="danger"
        title={t('tenant.documents.parties.deleteConfirmTitle')}
        message={
          pendingDeleteParty
            ? t('tenant.documents.parties.deleteConfirmBody', {
                identity: identityDisplay(pendingDeleteParty),
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteParty) {
            deleteMutation.mutate(pendingDeleteParty.id);
          }
        }}
        onClose={() => setPendingDeleteParty(null)}
      />
    </section>
  );
}
